<template>
  <div class="overview-container">
    <div class="page-header">
      <h2>客户资源池总览</h2>
      <el-button type="primary" size="small" @click="loadData" :loading="loading">
        <i class="el-icon-refresh"></i> 刷新数据
      </el-button>
    </div>

    <!-- 统计卡片 -->
    <div class="stat-cards">
      <div class="stat-card-item">
        <div class="stat-card stat-card-total" :class="{ active: currentFilter === 'all' }" @click="loadCustomerListByFilter('all')">
          <div class="stat-icon"><i class="el-icon-s-data"></i></div>
          <div class="stat-info">
            <div class="stat-value">{{ total }}</div>
            <div class="stat-label">客户总数</div>
          </div>
        </div>
      </div>
      <div class="stat-card-item">
        <div class="stat-card stat-card-covered" :class="{ active: currentFilter === 'covered' }" @click="loadCustomerListByFilter('covered')">
          <div class="stat-icon"><i class="el-icon-s-custom"></i></div>
          <div class="stat-info">
            <div class="stat-value">{{ coveredCount }}</div>
            <div class="stat-label">已覆盖客户</div>
          </div>
        </div>
      </div>
      <div class="stat-card-item">
        <div class="stat-card stat-card-uncovered" :class="{ active: currentFilter === 'unclaimed' }" @click="loadCustomerListByFilter('unclaimed')">
          <div class="stat-icon"><i class="el-icon-user-solid"></i></div>
          <div class="stat-info">
            <div class="stat-value">{{ uncoveredCount }}</div>
            <div class="stat-label">未覆盖客户</div>
          </div>
        </div>
      </div>
      <div class="stat-card-item">
        <div class="stat-card stat-card-coverage">
          <div class="stat-icon">
            <i class="el-icon-data-line"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ coverageRate }}%</div>
            <div class="stat-label">客户覆盖率</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 客户资源卡片列表 -->
    <div class="customer-list-section">
      <div class="section-header">
        <h3>客户资源列表</h3>
        <el-button type="text" @click="goToPotential()" size="small">查看全部 →</el-button>
      </div>
      <div class="customer-cards" v-loading="customerLoading">
        <div class="customer-card" :class="{'unclaimed': !isCovered(customer.claimStatus)}" v-for="(customer, index) in customerList" :key="customer.id || index" @click="openCustomerDetail(customer)">
          <div class="customer-icon">
            <img :src="getIndustryIcon(customer.industryName)" alt="行业图标" class="industry-icon" />
          </div>
          <div class="customer-info">
            <div class="customer-name">{{ customer.custName || '-' }}</div>
          </div>
          <el-tag
            class="status-tag"
            size="mini"
            effect="light"
            :type="isCovered(customer.claimStatus) ? 'success' : 'warning'"
          >{{ isCovered(customer.claimStatus) ? '已覆盖' : '未覆盖' }}</el-tag>
        </div>
        <div class="empty-state" v-if="customerList.length === 0 && !customerLoading">
          暂无客户数据
        </div>
      </div>
      <div class="pagination-wrapper" v-if="customerTotal > 0">
        <el-pagination
          background
          layout="prev, pager, next"
          :page-size="customerPageSize"
          :current-page.sync="customerPage"
          :total="customerTotal"
          @current-change="handleCustomerPageChange"
        ></el-pagination>
      </div>
    </div>

    <!-- 客户详情弹窗 -->
    <el-dialog
      :visible.sync="detailVisible"
      :title="currentCustomer.custName || '客户详情'"
      width="700px"
      :close-on-click-modal="false"
      class="customer-detail-dialog"
    >
      <div v-loading="detailLoading" class="customer-detail-content">
        <!-- 基本信息 -->
        <div class="detail-section">
          <div class="section-title">
            <i class="el-icon-info"></i> 基本信息
          </div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="客户名称">
              {{ currentCustomer.custName || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="所属行业">
              <el-tag size="small" v-if="currentCustomer.industryName">{{ currentCustomer.industryName }}</el-tag>
              <span v-else>-</span>
            </el-descriptions-item>
            <el-descriptions-item label="销售人员">
              {{ currentCustomer.custManagerName || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="所在地区" :span="2">
              {{ [currentCustomer.provinceName, currentCustomer.cityName, currentCustomer.districtName].filter(x => x).join(' ') || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="详细地址" :span="2">
              {{ currentCustomer.address || '-' }}
            </el-descriptions-item>
          </el-descriptions>
        </div>

        <!-- 联系人信息 -->
        <div class="detail-section" v-if="currentCustomer.linkmanName">
          <div class="section-title">
            <i class="el-icon-user"></i> 联系人信息
          </div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="联系人姓名">
              {{ currentCustomer.linkmanName || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="联系人职务">
              {{ currentCustomer.linkmanPost || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="联系电话">
              {{ currentCustomer.linkmanTel || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="联系邮箱">
              {{ currentCustomer.linkmanEmail || '-' }}
            </el-descriptions-item>
          </el-descriptions>
        </div>

        <!-- 客户状态 -->
        <div class="detail-section">
          <div class="section-title">
            <i class="el-icon-s-check"></i> 客户状态
          </div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="认领状态">
              <el-tag :type="currentCustomer.claimStatus ? 'success' : 'info'" size="small">
                {{ currentCustomer.claimStatus ? '已认领' : '未认领' }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="认领人" v-if="currentCustomer.claimUserName">
              {{ currentCustomer.claimUserName }}
            </el-descriptions-item>
            <el-descriptions-item label="认领时间" v-if="currentCustomer.claimTime">
              {{ formatTime(currentCustomer.claimTime) }}
            </el-descriptions-item>
          </el-descriptions>
        </div>

        <!-- 客户描述 -->
        <div class="detail-section" v-if="currentCustomer.custDesc">
          <div class="section-title">
            <i class="el-icon-document"></i> 客户描述
          </div>
          <div class="description-box">
            {{ currentCustomer.custDesc }}
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button type="warning" v-if="!currentCustomer.claimStatus" @click="goToClaim">去认领</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getIndustryStatistics, listPotentialCust } from '@/api/cust'

export default {
  data() {
    return {
      loading: false,
      total: 0,
      claimedCount: 0,
      unclaimedCount: 0,
      visitedCount: 0,
      customerList: [],
      customerTotal: 0,
      customerPage: 1,
      customerPageSize: 32,
      customerLoading: false,
      currentFilter: 'all',
      // 客户详情弹窗相关
      detailVisible: false,
      detailLoading: false,
      currentCustomer: {}
    }
  },
  computed: {
    coveredCount() {
      return this.claimedCount + this.visitedCount
    },
    uncoveredCount() {
      return this.total - this.coveredCount
    },
    coverageRate() {
      if (!this.total) return 0
      return ((this.coveredCount / this.total) * 100).toFixed(2)
    }
  },
  created() {
    this.loadData()
    this.loadCustomerList()
  },
  methods: {
    // 客户是否已被覆盖：claimStatus = 1(已认领) 或 3(已拜访)
    isCovered(status) {
      return status === 1 || status === 3
    },
    goToPotential(claimStatus) {
      const query = claimStatus !== undefined ? { claimStatus } : {}
      this.$router.push({ path: '/cust/potential', query })
    },

    // 打开客户详情弹窗
    openCustomerDetail(customer) {
      this.currentCustomer = { ...customer }
      this.detailVisible = true
    },

    // 跳转到完整详情页
    goToCustomerDetail() {
      this.$router.push({ path: '/cust/potential' })
      this.detailVisible = false
    },

    // 跳转到认领页面并搜索该客户
    goToClaim() {
      const custName = this.currentCustomer.custName || ''
      this.$router.push({ path: '/cust/potential', query: { custName } })
      this.detailVisible = false
    },

    // 根据行业名称获取对应的图标路径
    getIndustryIcon(industryName) {
      const iconMap = {
        '企业': require('@/assets/img/custType/enterprise.png'),
        '政府': require('@/assets/img/custType/government.png'),
        '教育': require('@/assets/img/custType/education.png'),
        '金融': require('@/assets/img/custType/finance.png'),
        '医疗': require('@/assets/img/custType/medical.png'),
        '个人': require('@/assets/img/custType/user.png'),
        '合作商': require('@/assets/img/custType/partner.png')
      }
      return iconMap[industryName] || require('@/assets/img/custType/user.png')
    },

    // 格式化时间
    formatTime(timeStr) {
      if (!timeStr) return '-'
      const date = new Date(timeStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },

    async loadCustomerList() {
      this.customerLoading = true
      try {
        const params = {
          page: this.customerPage,
          limit: this.customerPageSize
        }
        if (this.currentFilter === 'unclaimed') {
          params.claimStatus = 0
        } else if (this.currentFilter === 'covered') {
          params.claimStatus = '1,3'
        }
        const resp = await listPotentialCust(params)
        if (resp && resp.data) {
          this.customerList = resp.data || []
          this.customerTotal = resp.count || 0
        }
      } catch (e) {
        console.error('加载客户列表失败', e)
      } finally {
        this.customerLoading = false
      }
    },
    loadCustomerListByFilter(filter) {
      this.currentFilter = filter
      this.customerPage = 1
      this.loadCustomerList()
    },
    handleCustomerPageChange(page) {
      this.customerPage = page
      this.loadCustomerList()
    },
    async loadData() {
      this.loading = true
      try {
        const industryResp = await getIndustryStatistics()
        
        // 处理行业统计数据
        if (industryResp && industryResp.data) {
          const indData = industryResp.data
          if (indData.overview) {
            this.total = indData.overview.totalCount || 0
            this.claimedCount = indData.overview.claimedCount || 0
            this.unclaimedCount = indData.overview.unclaimedCount || 0
            this.visitedCount = indData.overview.visitedCount || 0
          }
        }
      } catch (e) {
        this.$message.error('获取数据失败')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.overview-container {
  padding: 20px;
  background: #fff;
  height: 83vh;
  overflow-y: auto;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 22px;
  color: #303133;
}

.stat-cards {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
}

.stat-card-item {
  flex: 1;
  min-width: 0;
}

.stat-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
  border: 2px solid transparent;
}

.stat-card.active {
  border-color: #409EFF;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon i {
  font-size: 28px;
  color: #fff;
}

.stat-card-total .stat-icon { background: linear-gradient(135deg, #409EFF, #66b1ff); }
.stat-card-covered .stat-icon { background: linear-gradient(135deg, #67c23a, #85ce61); }
.stat-card-uncovered .stat-icon { background: linear-gradient(135deg, #e6a23c, #f0c78a); }
.stat-card-coverage .stat-icon { background: linear-gradient(135deg, #ff6b6b, #ffa502); }
.stat-card-coverage { cursor: default; }

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #303133;
  line-height: 1.2;
}

.stat-label {
  font-size: 14px;
  color: #909399;
  margin-top: 4px;
}

.customer-list-section {
  margin-bottom: 24px;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
}

.customer-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 8px;
}

.customer-card {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: #f5f7fa;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
  height: 52px;
}

.customer-card:hover {
  background: #ecf5ff;
  border-color: #409EFF;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
}

.customer-card.unclaimed {
  background: #fff7e6;
  border-color: #faad14;
}

.customer-card.unclaimed:hover {
  background: #ffe7ba;
  border-color: #fa8c16;
  box-shadow: 0 2px 8px rgba(250, 140, 22, 0.15);
}

.customer-icon {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background: linear-gradient(135deg, #409EFF, #66b1ff);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  padding: 6px;
}

.industry-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.customer-info {
  flex: 1;
  min-width: 0;
}

.status-tag {
  flex-shrink: 0;
  margin-left: 4px;
  font-size: $font-size-md;
}

.customer-name {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
  line-height: 1.4;
  word-break: break-all;
  overflow-wrap: break-word;
}

.customer-industry .el-tag {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 0;
  color: #909399;
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

/* 客户详情弹窗样式 */
.customer-detail-dialog .el-dialog__body {
  padding: 20px;
}

.customer-detail-content {
  max-height: 60vh;
  overflow-y: auto;
}

.detail-section {
  margin-bottom: 24px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #ecf5ff;
  display: flex;
  align-items: center;
  gap: 6px;
}

.section-title i {
  color: #409EFF;
  font-size: 16px;
}

.description-box {
  background: #f5f7fa;
  padding: 16px;
  border-radius: 8px;
  line-height: 1.8;
  color: #606266;
}

.customer-detail-dialog .el-descriptions__label {
  width: 110px;
}

.customer-detail-dialog .dialog-footer {
  padding-top: 10px;
}
</style>
